// The Allocation Plan importer (PRD §8 P1, Appendix B). Paste the table from Excel; the screen finds
// its columns, matches projects and people, and shows every match for correction. Nothing is saved
// until Import, which goes through the same endpoints as the editor: each project is created or
// updated, staffed, and booked (taking Baseline 1) one at a time, with the result of each shown.

import { forgetCodeLabels, useCodeLabels } from "../codes";
import { useEffect, useMemo, useState } from "react";
import { Badge, Body1, Button, Caption1, Card, Input, Select, Subtitle2, Textarea } from "@fluentui/react-components";
import {
  codeIn,
  detectLayout,
  importedPlan,
  isClosed,
  isValidCode,
  matchPerson,
  matchRole,
  nameWithoutCode,
  newId,
  normalizeCode,
  parsePasted,
  readLines,
  statusFromText,
  STATUS_LABEL,
  type ImportLayout,
  type ImportLine,
  type Project,
  type ProjectStatus,
} from "../../../core";
import { api, errorText, send, useApi, type CodesResponse, type PeopleNamesResponse, type ProjectResponse, type ProjectsResponse, type ReferenceResponse, type Stored } from "../api";
import { href } from "../router";
import { ErrorBar, Loading, Page, useMe, Warnings } from "../ui";

type Column = "status" | "code" | "project" | "resource" | "role";
const COLUMNS: { key: Column; label: string }[] = [
  { key: "status", label: "Status" },
  { key: "code", label: "Project code" },
  { key: "project", label: "Project" },
  { key: "resource", label: "Resource" },
  { key: "role", label: "Role" },
];
const CREATE_STATUSES: ProjectStatus[] = ["prospect", "opportunity", "pending", "booked"];

interface Group {
  text: string;
  status: string;
  lines: ImportLine[];
}
interface ProjectChoice {
  action: "skip" | "update" | "create";
  projectId: string;
  code: string;
  name: string;
  status: ProjectStatus;
}
interface PersonChoice {
  personId: string | null;
  roleId: string;
}
interface Result {
  ok: boolean;
  text: string;
  projectId?: string;
}

const letter = (i: number) => String.fromCharCode(65 + (i % 26)).repeat(Math.floor(i / 26) + 1);
const monthText = (m: string) => m.slice(0, 7);

export function ImportPlan() {
  const { label: codeLabel } = useCodeLabels();
  const me = useMe();
  const projects = useApi<ProjectsResponse>("/projects");
  const codes = useApi<CodesResponse>("/project-codes");
  const people = useApi<PeopleNamesResponse>("/people?view=names");
  const ref = useApi<ReferenceResponse>("/reference");
  const [text, setText] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [columns, setColumns] = useState<Partial<Record<Column, number | null>>>({});
  const [projectEdits, setProjectEdits] = useState<Record<string, Partial<ProjectChoice>>>({});
  const [personEdits, setPersonEdits] = useState<Record<string, Partial<PersonChoice>>>({});
  const [results, setResults] = useState<Result[]>([]);
  const [running, setRunning] = useState(false);

  // A new paste starts fresh.
  useEffect(() => {
    setColumns({});
    setProjectEdits({});
    setPersonEdits({});
    setResults([]);
  }, [text]);

  const rows = useMemo(() => parsePasted(text), [text]);
  const detected = useMemo(() => (text.trim() ? detectLayout(rows, Number(year) || new Date().getFullYear()) : null), [rows, text, year]);
  const layout: ImportLayout | null = useMemo(() => detected && { ...detected, ...columns }, [detected, columns]);
  const read = useMemo(() => (layout ? readLines(rows, layout) : null), [rows, layout]);

  const existing = (projects.data?.projects ?? []).filter((p) => !p.isTemplate);
  const roles = (ref.data?.roles.roles ?? []).filter((r) => r.active);
  const peopleList = people.data?.people ?? [];

  const groups: Group[] = useMemo(() => {
    const out = new Map<string, Group>();
    for (const l of read?.lines ?? []) {
      const g = out.get(l.project) ?? { text: l.project, status: "", lines: [] };
      g.lines.push(l);
      if (!g.status && l.status) g.status = l.status;
      out.set(l.project, g);
    }
    return [...out.values()];
  }, [read]);

  const known = new Set([...existing.map((p) => p.code), ...(codes.data?.codes ?? []).map((c) => c.code)]);
  const projectDefault = (g: Group): ProjectChoice => {
    const code = codeIn(g.text, known);
    const match = existing.find((p) => (code && p.code === code) || p.name.toLowerCase() === g.text.toLowerCase());
    const status = statusFromText(g.status);
    if (match) return { action: match.hours > 0 ? "skip" : "update", projectId: match.id, code: match.code, name: match.name, status: match.status };
    const closed = !!status && isClosed(status);
    return { action: closed ? "skip" : "create", projectId: "", code: code ?? "", name: nameWithoutCode(g.text, code) || g.text, status: status && !closed ? status : "prospect" };
  };
  const projectChoice = (g: Group): ProjectChoice => ({ ...projectDefault(g), ...projectEdits[g.text] });

  const resources = useMemo(() => [...new Set(groups.flatMap((g) => g.lines.map((l) => l.resource)))], [groups]);
  const personDefault = (resource: string): PersonChoice => {
    const personId = matchPerson(resource, peopleList);
    const roleText = groups.flatMap((g) => g.lines).find((l) => l.resource === resource)?.role ?? "";
    const roleId = peopleList.find((p) => p.id === personId)?.roleId ?? matchRole(`${roleText} ${resource}`, roles) ?? roles[0]?.id ?? "";
    return { personId, roleId };
  };
  const personChoice = (resource: string): PersonChoice => ({ ...personDefault(resource), ...personEdits[resource] });

  // What would be imported, and what stops it.
  const chosen = groups.filter((g) => projectChoice(g).action !== "skip");
  const problems: string[] = [];
  const newCodes = new Map<string, number>();
  for (const g of chosen) {
    const c = projectChoice(g);
    if (c.action === "create") {
      const code = normalizeCode(c.code);
      if (!isValidCode(code)) problems.push(`"${g.text}" needs a project code, like LG002.`);
      else if (existing.some((p) => p.code === code)) problems.push(`${code} is already a project; choose "Replace staffing" for it instead.`);
      newCodes.set(code, (newCodes.get(code) ?? 0) + 1);
      if (!c.name.trim()) problems.push(`"${g.text}" needs a name.`);
    } else if (!c.projectId) problems.push(`Pick which project "${g.text}" replaces staffing in.`);
  }
  for (const [code, n] of newCodes) if (n > 1 && isValidCode(code)) problems.push(`${code} would be created ${n} times.`);
  if (chosen.some((g) => g.lines.some((l) => !personChoice(l.resource).roleId))) problems.push("Every row needs a role.");

  async function run() {
    setRunning(true);
    const done: Result[] = [];
    for (const g of chosen) {
      const c = projectChoice(g);
      const rowsFor = g.lines.map((l) => ({ ...personChoice(l.resource), hours: l.hours }));
      try {
        if (c.action === "create") {
          const code = normalizeCode(c.code);
          const listed = codes.data?.codes.find((x) => x.code === code);
          const plan = importedPlan(rowsFor, null, newId);
          forgetCodeLabels(); // the next screen reads the codes afresh, this one with them
          const { project } = await send<{ project: Stored<Project> }>("POST", "/projects", {
            project: { code, name: c.name.trim(), clientName: listed?.client ?? "", status: c.status === "booked" ? "pending" : c.status, billable: !/internal|3seven/i.test(listed?.client ?? ""), isTemplate: false },
            startMonday: plan.startMonday,
            weeks: plan.weeks,
          });
          const { project: saved } = await send<{ project: Stored<Project> }>("PUT", `/projects/${encodeURIComponent(project.id)}`, { etag: project._etag, project: { ...details(project), plan } });
          if (c.status === "booked") await send("POST", `/projects/${encodeURIComponent(project.id)}/status`, { etag: saved._etag, status: "booked" });
          done.push({ ok: true, projectId: project.id, text: `Created ${code} · ${c.name.trim()} (${STATUS_LABEL[c.status].toLowerCase()}), ${g.lines.length} ${g.lines.length === 1 ? "row" : "rows"}${c.status === "booked" ? ", Baseline 1 taken" : ""}.` });
        } else {
          const { project } = await api<ProjectResponse>(`/projects/${encodeURIComponent(c.projectId)}`);
          const plan = importedPlan(rowsFor, project.plan, newId);
          await send("PUT", `/projects/${encodeURIComponent(project.id)}`, { etag: project._etag, project: { ...details(project), plan } });
          done.push({ ok: true, projectId: project.id, text: `Replaced the staffing in ${project.code} · ${project.name} with ${g.lines.length} ${g.lines.length === 1 ? "row" : "rows"}.` });
        }
      } catch (err) {
        done.push({ ok: false, text: `${g.text}: ${errorText(err)}` });
      }
      setResults([...done]);
    }
    setRunning(false);
    projects.reload();
  }

  const loading = projects.loading || people.loading || ref.loading;
  return (
    <Page title="Import the Allocation Plan">
      <a href={href("projects")} className="back">
        ← Projects
      </a>
      {!me.can("plans.edit") && <Warnings items={["Importing needs the plans.edit capability."]} />}
      <ErrorBar message={projects.error ?? people.error ?? ref.error} />

      <Card className="panel wide">
        <Subtitle2>1. Paste the table</Subtitle2>
        <Caption1>
          In Excel, select the Allocation Plan from its header row (Status, Project, Resource and the month columns) to its last row,
          copy it, and paste it here. Each month column holds average hours a week. Nothing is saved until you import.
        </Caption1>
        <Textarea value={text} onChange={(_, x) => setText(x.value)} resize="vertical" rows={6} placeholder="Paste here" aria-label="Pasted Allocation Plan" />
        <label className="inline-field">
          <Caption1>Year for month headers without one</Caption1>
          <Input size="small" type="number" value={year} onChange={(_, x) => setYear(x.value)} className="count-input" aria-label="Year for month headers without one" />
        </label>
        {text.trim() && !detected && <Warnings items={["No header row with a Project column and month columns was found in the first 15 rows. Copy from the header row down."]} />}
      </Card>

      {layout && read && (
        <>
          <Card className="panel wide">
            <Subtitle2>2. Check the columns</Subtitle2>
            <Caption1>
              Header row {layout.header + 1}. {layout.months.length} month {layout.months.length === 1 ? "column" : "columns"}:{" "}
              {monthText(layout.months[0].month)} – {monthText(layout.months.at(-1)!.month)}. {read.lines.length} rows with hours
              {read.skipped ? `; ${read.skipped} blank, total or all-zero rows left out` : ""}.
            </Caption1>
            <div className="toolbar">
              {COLUMNS.map((c) => (
                <label key={c.key} className="inline-field">
                  <Caption1>{c.label}</Caption1>
                  <Select
                    size="small"
                    value={layout[c.key] === null ? "" : String(layout[c.key])}
                    onChange={(_, x) => setColumns((all) => ({ ...all, [c.key]: x.value === "" ? null : Number(x.value) }))}
                    aria-label={`${c.label} column`}
                  >
                    <option value="">None</option>
                    {rows[layout.header].map((h, i) =>
                      layout.months.some((m) => m.col === i) ? null : (
                        <option key={i} value={i}>
                          {letter(i)}: {h || "(blank)"}
                        </option>
                      ),
                    )}
                  </Select>
                </label>
              ))}
            </div>
          </Card>

          {loading ? (
            <Loading />
          ) : (
            <>
              <Card className="panel wide">
                <Subtitle2>3. Match projects</Subtitle2>
                <Caption1>
                  A project already in the app is skipped if it has staffing; choose Replace staffing to overwrite it with the workbook's rows
                  (its payments and costs stay). A booked project is created, staffed, then booked, which takes Baseline 1.
                </Caption1>
                <div className="table-scroll">
                  <table className="edit-table">
                    <thead>
                      <tr>
                        <th>In the workbook</th>
                        <th className="num">Rows</th>
                        <th>Do</th>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((g) => {
                        const c = projectChoice(g);
                        const edit = (patch: Partial<ProjectChoice>) => setProjectEdits((all) => ({ ...all, [g.text]: { ...all[g.text], ...patch } }));
                        return (
                          <tr key={g.text}>
                            <td>
                              {g.text}
                              {g.status && <Caption1 className="block muted">{g.status}</Caption1>}
                            </td>
                            <td className="num">{g.lines.length}</td>
                            <td>
                              <Select size="small" value={c.action} onChange={(_, x) => edit({ action: x.value as ProjectChoice["action"] })} aria-label={`What to do with ${g.text}`}>
                                <option value="create">Create a project</option>
                                <option value="update">Replace staffing in…</option>
                                <option value="skip">Skip</option>
                              </Select>
                            </td>
                            {c.action === "update" ? (
                              <td colSpan={3}>
                                <Select size="small" value={c.projectId} onChange={(_, x) => edit({ projectId: x.value })} aria-label={`Project for ${g.text}`}>
                                  <option value="">Pick a project</option>
                                  {existing.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {codeLabel(p.code)} — {p.name}
                                      {p.hours > 0 ? " (has staffing)" : ""}
                                    </option>
                                  ))}
                                </Select>
                              </td>
                            ) : c.action === "create" ? (
                              <>
                                <td>
                                  <Input size="small" className="count-input" value={c.code} onChange={(_, x) => edit({ code: x.value.toUpperCase() })} aria-label={`Code for ${g.text}`} list="import-codes" />
                                </td>
                                <td>
                                  <Input size="small" value={c.name} onChange={(_, x) => edit({ name: x.value })} aria-label={`Name for ${g.text}`} />
                                </td>
                                <td>
                                  <Select size="small" value={c.status} onChange={(_, x) => edit({ status: x.value as ProjectStatus })} aria-label={`Status for ${g.text}`}>
                                    {CREATE_STATUSES.map((s) => (
                                      <option key={s} value={s}>
                                        {STATUS_LABEL[s]}
                                      </option>
                                    ))}
                                  </Select>
                                </td>
                              </>
                            ) : (
                              <td colSpan={3}>
                                <Caption1 className="muted">{c.projectId ? "Already in the app with staffing." : statusFromText(g.status) && isClosed(statusFromText(g.status)!) ? "Closed in the workbook." : "Not imported."}</Caption1>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <datalist id="import-codes">
                    {(codes.data?.codes ?? []).filter((x) => !existing.some((p) => p.code === x.code)).map((x) => (
                      <option key={x.code} value={x.code}>
                        {x.client}
                      </option>
                    ))}
                  </datalist>
                </div>
              </Card>

              <Card className="panel wide">
                <Subtitle2>4. Match people</Subtitle2>
                <Caption1>A resource nobody matches becomes a placeholder, costed at its role's standard rate until someone is named.</Caption1>
                <div className="table-scroll">
                  <table className="edit-table">
                    <thead>
                      <tr>
                        <th>In the workbook</th>
                        <th>Person</th>
                        <th>Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resources.map((r) => {
                        const c = personChoice(r);
                        const matched = matchPerson(r, peopleList);
                        const edit = (patch: Partial<PersonChoice>) => setPersonEdits((all) => ({ ...all, [r]: { ...all[r], ...patch } }));
                        return (
                          <tr key={r}>
                            <td>
                              {r || <span className="muted">(blank)</span>}
                              {!matched && c.personId === null && (
                                <Badge appearance="tint" color="informative" size="small" className="in-effect">
                                  Placeholder
                                </Badge>
                              )}
                            </td>
                            <td>
                              <Select
                                size="small"
                                value={c.personId ?? ""}
                                onChange={(_, x) => {
                                  const person = peopleList.find((p) => p.id === x.value);
                                  edit({ personId: x.value || null, ...(person?.roleId ? { roleId: person.roleId } : {}) });
                                }}
                                aria-label={`Person for ${r}`}
                              >
                                <option value="">Placeholder</option>
                                {peopleList.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name}
                                    {p.status === "archived" ? " (archived)" : ""}
                                  </option>
                                ))}
                              </Select>
                            </td>
                            <td>
                              <Select size="small" value={c.roleId} onChange={(_, x) => edit({ roleId: x.value })} aria-label={`Role for ${r}`}>
                                {roles.map((role) => (
                                  <option key={role.id} value={role.id}>
                                    {role.name}
                                  </option>
                                ))}
                              </Select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card className="panel wide">
                <Subtitle2>5. Import</Subtitle2>
                <Warnings items={problems} />
                <div className="toolbar">
                  <Button appearance="primary" disabled={!me.can("plans.edit") || running || !chosen.length || problems.length > 0} onClick={() => void run()}>
                    {running ? "Importing…" : `Import ${chosen.length} ${chosen.length === 1 ? "project" : "projects"}`}
                  </Button>
                  <Caption1 className="muted">{groups.length - chosen.length} skipped.</Caption1>
                </div>
                {results.length > 0 && (
                  <ul className="plain-list" aria-label="Import results">
                    {results.map((r, i) => (
                      <li key={i}>
                        <Badge appearance="tint" color={r.ok ? "success" : "danger"} size="small">
                          {r.ok ? "Done" : "Failed"}
                        </Badge>{" "}
                        {r.projectId ? <a href={href("projects", r.projectId)}>{r.text}</a> : <Body1>{r.text}</Body1>}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </>
          )}
        </>
      )}
    </Page>
  );
}

/** A project's details as the save endpoint takes them, alongside a new plan. */
const details = (p: Project) => ({
  code: p.code,
  name: p.name,
  clientName: p.clientName,
  billable: p.billable,
  isTemplate: p.isTemplate,
  offering: p.offering,
  notes: p.notes,
  targetMargin: p.targetMargin,
});
